import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Spinner,
  Card,
  Row,
  Col,
  Badge,
  ListGroup,
  Button,
  Collapse,
} from "react-bootstrap";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import multiMonthPlugin from "@fullcalendar/multimonth";
import esLocale from "@fullcalendar/core/locales/es";
import moment from "moment";
import { getTrainingsCalendar } from "../../redux/capacitacionDesarrollo/campaigns/actions";

const CalendarCampaigns = () => {
  // Estados
  const dispatch = useDispatch();
  const { trainingsCalendar, loaderTrainingsCalendar } = useSelector(
    (state) => state.campaigns
  );
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showCollaborators, setShowCollaborators] = useState(false);

  //Store
  const userCode = useSelector((store) => store.auth.username);

  const handleDatesSet = (arg) => {
    setSelectedEvent(null);

    const filters = {
      fecha_inicio: moment(arg.start).format("YYYY-MM-DD"),
      fecha_fin: moment(arg.end).format("YYYY-MM-DD"),
      codigo_empleado: userCode,
    };
    dispatch(getTrainingsCalendar(filters));
  };

  const transformTrainingsToEvents = () => {
    if (!trainingsCalendar?.results) return [];

    return trainingsCalendar.results
      .map((training) => ({
        id: training.id,
        title: training.nombre_evento_capacitacion,
        start: training.fecha_inicio
          ? moment(training.fecha_inicio).format("YYYY-MM-DD")
          : null,
        extendedProps: {
          ...training,
        },
        backgroundColor: training.estado?.color || "#3788d8",
        borderColor: training.estado?.color || "#3788d8",
        textColor: "#ffffff",
      }))
      .filter((event) => event.start);
  };

  const handleEventClick = (info) => {
    setSelectedEvent(info.event.extendedProps);
    setShowCollaborators(false);
  };

  const handleCloseDetails = () => {
    setSelectedEvent(null);
  };

  const toggleCollaborators = () => {
    setShowCollaborators(!showCollaborators);
  };

  return (
    <div className="p-3">
      {loaderTrainingsCalendar && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "5px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Spinner animation="border" role="status" />
            <span style={{ marginLeft: "10px" }}>
              Cargando capacitaciones...
            </span>
          </div>
        </div>
      )}
      <Row>
        <Col md={selectedEvent ? 8 : 12}>
          <FullCalendar
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              interactionPlugin,
              multiMonthPlugin,
            ]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay,multiMonthYear",
            }}
            events={transformTrainingsToEvents()}
            eventClick={handleEventClick}
            locale={esLocale}
            height="auto"
            contentHeight="auto"
            eventDisplay="block"
            editable={false}
            selectable={false}
            weekends={true}
            datesSet={handleDatesSet}
            viewDidMount={() => setSelectedEvent(null)}
            eventTimeFormat={{
              hour: "2-digit",
              minute: "2-digit",
              meridiem: false,
            }}
          />
        </Col>

        {selectedEvent && (
          <Col md={4}>
            <Card className="shadow-sm">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Detalles del Evento</h5>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleCloseDetails}
                >
                  &times;
                </button>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <h4>{selectedEvent.nombre_evento_capacitacion}</h4>
                  <Badge
                    pill
                    style={{
                      backgroundColor: selectedEvent.estado?.color,
                      color: "white",
                    }}
                  >
                    {selectedEvent.estado?.descripcion}
                  </Badge>
                </div>

                <Row className="mb-2">
                  <Col xs={6}>
                    <strong>Fecha inicio:</strong>
                    <p>
                      {moment(selectedEvent.fecha_inicio).format("DD/MM/YYYY")}
                    </p>
                  </Col>
                  <Col xs={6}>
                    <strong>Fecha fin:</strong>
                    <p>
                      {moment(selectedEvent.fecha_fin).format("DD/MM/YYYY")}
                    </p>
                  </Col>
                </Row>

                <div className="mb-2">
                  <strong>Hora:</strong>
                  <p>{selectedEvent.hora_capacitacion || "No especificada"}</p>
                </div>

                <div className="mb-2">
                  <strong>Lugar:</strong>
                  <p>{selectedEvent.lugar || "No especificado"}</p>
                </div>

                {selectedEvent.responsable && (
                  <div className="mb-2">
                    <strong>Responsable:</strong>
                    <p>
                      {selectedEvent.responsable.nombre} (
                      {selectedEvent.responsable.codigo})
                    </p>
                  </div>
                )}

                <div className="mb-2">
                  <strong>Objetivo:</strong>
                  <p>
                    {selectedEvent.objetivo_capacitacion || "No especificado"}
                  </p>
                </div>

                {selectedEvent.convocados &&
                selectedEvent.convocados.length > 0 ? (
                  <div className="mb-2">
                    <Button
                      variant="link"
                      onClick={toggleCollaborators}
                      className="p-0 text-decoration-none"
                    >
                      Colaboradores convocados (
                      {selectedEvent.convocados.length}){" "}
                      <i className={`material-icons`}>
                        {showCollaborators ? "expand_less" : "expand_more"}
                      </i>
                    </Button>

                    <Collapse in={showCollaborators}>
                      <div>
                        <div
                          style={{
                            maxHeight: "200px",
                            overflowY: "auto",
                            marginTop: "10px",
                          }}
                        >
                          <ListGroup variant="flush">
                            {selectedEvent.convocados.map(
                              (colaborador, index) => (
                                <ListGroup.Item
                                  key={index}
                                  className="py-2 px-1"
                                >
                                  {colaborador.empleado.nombre} (
                                  {colaborador.empleado.codigo})
                                </ListGroup.Item>
                              )
                            )}
                          </ListGroup>
                        </div>
                      </div>
                    </Collapse>
                  </div>
                ) : (
                  <div className="mb-2">
                    <strong>Colaboradores convocados:</strong>
                    <p>No hay colaboradores convocados</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default CalendarCampaigns;
